import { indexer } from "@/lib/algo";
import { makeDSClient } from "@/lib/clients";
import { queryClient } from "@/lib/query-client";
import { N } from "@/lib/utils";
import { EmittedArc28EventAndTransaction } from "@myth-finance/dualstake-ts-sdk";
import { useQuery } from "@tanstack/react-query";
import { getApplicationAddress, indexerModels } from "algosdk";

export interface HistoryEvent extends EmittedArc28EventAndTransaction {
  meta?: {
    staked?: number;
  };
}

interface DSTransactionResponse {
  round: number;
  events: HistoryEvent[];
}

function findPrevStaked(arr: HistoryEvent[], start: number) {
  for (let i = start - 1; i >= 0; i--) {
    if (arr[i]?.meta?.staked !== undefined) return arr[i]?.meta?.staked;
  }
}

export function addStakedMetaToEvents({
  events,
  mode,
  activeAddress,
  activityEventNames,
}: {
  events: HistoryEvent[];
  mode: "GLOBAL" | "PERSONAL";
  activeAddress?: string;
  activityEventNames: Set<string>;
}): HistoryEvent[] {
  return events
    .filter((e) => {
      const { name } = e.spec;
      if (!activityEventNames.has(name)) return;
      if (mode === "PERSONAL" && e.outerTransaction.sender !== activeAddress)
        return;
      return true;
    })
    .reverse()
    .map((e, i, all) => {
      const { name } = e.spec;
      if (name === "mint") {
        const prev = findPrevStaked(all, i) ?? 0;
        e.meta = e.meta ?? {};
        e.meta.staked = prev + N(e.argsByName.algo_amount);
      } else if (name === "redeem") {
        const prev = findPrevStaked(all, i) ?? 0;
        e.meta = e.meta ?? {};
        e.meta.staked = Math.max(0, prev - N(e.argsByName.algo_amount));
      }
      return e;
    });
}

export const useHistoryEvents = (appId: bigint) => {
  const queryKey = ["ds-events", String(appId)];
  return useQuery({
    queryKey,
    gcTime: Infinity,
    staleTime: 60 * 1_000,
    enabled: !!appId,
    queryFn: async (): Promise<DSTransactionResponse | null> => {
      const cached = queryClient.getQueryData(
        queryKey
      ) as DSTransactionResponse | null;
      const prevData = cached ?? { round: 0, events: [] };
      const escrow = getApplicationAddress(appId);
      let query = indexer
        .searchForTransactions()
        .address(escrow)
        .limit(2000)
        .addressRole("sender");
      if (cached?.round) query = query.minRound(cached.round + 1);
      const response = await query.do();
      let data =
        indexerModels.TransactionsResponse.from_obj_for_encoding(response);
      const round = Number(data.currentRound);
      let { transactions } = data;
      while (data.nextToken !== undefined) {
        const response = await query.nextToken(data.nextToken!).do();
        data =
          indexerModels.TransactionsResponse.from_obj_for_encoding(response);
        transactions.push(...data.transactions);
      }

      const retVals: EmittedArc28EventAndTransaction[] = [];

      const client = makeDSClient(appId);
      for (const transaction of transactions) {
        const events = client.getParsedArc28EventsFromTransaction(transaction);
        if (events.length) retVals.push(...events);
      }
      return {
        round,
        events: [...retVals, ...prevData.events],
      };
    },
  });
};

export const clearHistoryEvents = (appId?: bigint) => {
  const queryKey = ["ds-events"];
  if (appId) queryKey.push(String(appId));
  queryClient.invalidateQueries({ queryKey });
};
